'use client';

import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// Types
// ============================================================================

export type TabsOrientation = 'horizontal' | 'vertical';
export type TabsVariant = 'default' | 'pills' | 'underline' | 'enclosed';
export type TabsSize = 'sm' | 'md' | 'lg';

export interface TabItem {
  id: string;
  label: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
  icon?: React.ReactNode;
}

// ============================================================================
// Tabs Context
// ============================================================================

interface TabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
  orientation: TabsOrientation;
  variant: TabsVariant;
  size: TabsSize;
}

const TabsContext = React.createContext<TabsContextValue | null>(null);

export function useTabsContext() {
  const context = React.useContext(TabsContext);
  if (!context) {
    throw new Error('useTabsContext must be used within Tabs');
  }
  return context;
}

// ============================================================================
// Tabs
// ============================================================================

export interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  orientation?: TabsOrientation;
  variant?: TabsVariant;
  size?: TabsSize;
  children: React.ReactNode;
}

export function Tabs({
  value: controlledValue,
  defaultValue = '',
  onValueChange,
  orientation = 'horizontal',
  variant = 'default',
  size = 'md',
  className,
  children,
  ...props
}: TabsProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue);

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  const handleValueChange = React.useCallback(
    (newValue: string) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [isControlled, onValueChange]
  );

  const contextValue: TabsContextValue = {
    value,
    onValueChange: handleValueChange,
    orientation,
    variant,
    size,
  };

  return (
    <TabsContext.Provider value={contextValue}>
      <div
        className={clsx(
          'tabs',
          orientation === 'vertical' && 'flex',
          className
        )}
        {...props}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
}

// ============================================================================
// TabsList
// ============================================================================

export interface TabsListProps extends React.HTMLAttributes<HTMLDivElement> {}

export function TabsList({ className, children, ...props }: TabsListProps) {
  const { orientation, variant } = useTabsContext();

  const variantClasses = {
    default: 'bg-gray-100 dark:bg-gray-800 rounded-lg p-1',
    pills: 'gap-2',
    underline: 'border-b border-gray-200 dark:border-gray-700',
    enclosed: 'border-b border-gray-200 dark:border-gray-700',
  };

  return (
    <div
      role="tablist"
      aria-orientation={orientation}
      className={clsx(
        'tabs-list flex',
        orientation === 'vertical' ? 'flex-col' : 'flex-row',
        variantClasses[variant],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// TabsTrigger
// ============================================================================

export interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
  icon?: React.ReactNode;
}

export function TabsTrigger({
  value,
  icon,
  disabled,
  className,
  children,
  ...props
}: TabsTriggerProps) {
  const { value: selectedValue, onValueChange, variant, size } = useTabsContext();
  const isSelected = value === selectedValue;

  const sizeClasses = {
    sm: 'px-2 py-1 text-sm',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base',
  };

  const variantClasses = {
    default: clsx(
      'rounded-md transition-colors',
      isSelected
        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow'
        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
    ),
    pills: clsx(
      'rounded-full transition-colors',
      isSelected
        ? 'bg-blue-600 text-white'
        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
    ),
    underline: clsx(
      'border-b-2 -mb-px transition-colors',
      isSelected
        ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
    ),
    enclosed: clsx(
      'border border-b-0 rounded-t-md -mb-px transition-colors',
      isSelected
        ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'
        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
    ),
  };

  return (
    <button
      role="tab"
      type="button"
      aria-selected={isSelected}
      aria-controls={`tabpanel-${value}`}
      tabIndex={isSelected ? 0 : -1}
      disabled={disabled}
      onClick={() => onValueChange(value)}
      className={clsx(
        'tabs-trigger inline-flex items-center justify-center gap-2 font-medium',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        sizeClasses[size],
        variantClasses[variant],
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      {...props}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </button>
  );
}

// ============================================================================
// TabsContent
// ============================================================================

export interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  forceMount?: boolean;
}

export function TabsContent({
  value,
  forceMount = false,
  className,
  children,
  ...props
}: TabsContentProps) {
  const { value: selectedValue } = useTabsContext();
  const isSelected = value === selectedValue;

  if (!forceMount && !isSelected) return null;

  return (
    <div
      role="tabpanel"
      id={`tabpanel-${value}`}
      aria-labelledby={`tab-${value}`}
      hidden={!isSelected}
      tabIndex={0}
      className={clsx(
        'tabs-content focus:outline-none',
        !isSelected && 'hidden',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// Accordion Context
// ============================================================================

type AccordionType = 'single' | 'multiple';

interface AccordionContextValue {
  type: AccordionType;
  value: string[];
  onValueChange: (value: string) => void;
  collapsible: boolean;
}

const AccordionContext = React.createContext<AccordionContextValue | null>(null);

export function useAccordionContext() {
  const context = React.useContext(AccordionContext);
  if (!context) {
    throw new Error('useAccordionContext must be used within Accordion');
  }
  return context;
}

// ============================================================================
// Accordion
// ============================================================================

export interface AccordionSingleProps extends React.HTMLAttributes<HTMLDivElement> {
  type: 'single';
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  collapsible?: boolean;
  children: React.ReactNode;
}

export interface AccordionMultipleProps extends React.HTMLAttributes<HTMLDivElement> {
  type: 'multiple';
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
  children: React.ReactNode;
}

export type AccordionProps = AccordionSingleProps | AccordionMultipleProps;

export function Accordion(props: AccordionProps) {
  const {
    type,
    className,
    children,
    ...rest
  } = props;

  if (type === 'single') {
    return <AccordionSingle {...props as AccordionSingleProps} />;
  }

  return <AccordionMultiple {...props as AccordionMultipleProps} />;
}

function AccordionSingle({
  value: controlledValue,
  defaultValue = '',
  onValueChange,
  collapsible = false,
  className,
  children,
  ...props
}: Omit<AccordionSingleProps, 'type'>) {
  const [internalValue, setInternalValue] = React.useState(defaultValue);

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  const handleValueChange = React.useCallback(
    (itemValue: string) => {
      const newValue = value === itemValue && collapsible ? '' : itemValue;
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [isControlled, value, collapsible, onValueChange]
  );

  const contextValue: AccordionContextValue = {
    type: 'single',
    value: value ? [value] : [],
    onValueChange: handleValueChange,
    collapsible,
  };

  return (
    <AccordionContext.Provider value={contextValue}>
      <div className={clsx('accordion', className)} {...props}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

function AccordionMultiple({
  value: controlledValue,
  defaultValue = [],
  onValueChange,
  className,
  children,
  ...props
}: Omit<AccordionMultipleProps, 'type'>) {
  const [internalValue, setInternalValue] = React.useState<string[]>(defaultValue);

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  const handleValueChange = React.useCallback(
    (itemValue: string) => {
      const newValue = value.includes(itemValue)
        ? value.filter((v) => v !== itemValue)
        : [...value, itemValue];

      if (!isControlled) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [isControlled, value, onValueChange]
  );

  const contextValue: AccordionContextValue = {
    type: 'multiple',
    value,
    onValueChange: handleValueChange,
    collapsible: true,
  };

  return (
    <AccordionContext.Provider value={contextValue}>
      <div className={clsx('accordion', className)} {...props}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

// ============================================================================
// AccordionItem
// ============================================================================

interface AccordionItemContextValue {
  value: string;
  isOpen: boolean;
  disabled: boolean;
}

const AccordionItemContext = React.createContext<AccordionItemContextValue | null>(null);

function useAccordionItemContext() {
  const context = React.useContext(AccordionItemContext);
  if (!context) {
    throw new Error('useAccordionItemContext must be used within AccordionItem');
  }
  return context;
}

export interface AccordionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  disabled?: boolean;
}

export function AccordionItem({
  value,
  disabled = false,
  className,
  children,
  ...props
}: AccordionItemProps) {
  const { value: accordionValue } = useAccordionContext();
  const isOpen = accordionValue.includes(value);

  const itemContextValue: AccordionItemContextValue = {
    value,
    isOpen,
    disabled,
  };

  return (
    <AccordionItemContext.Provider value={itemContextValue}>
      <div
        className={clsx(
          'accordion-item border-b border-gray-200 dark:border-gray-700',
          'last:border-b-0',
          className
        )}
        {...props}
      >
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
}

// ============================================================================
// AccordionTrigger
// ============================================================================

export interface AccordionTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

export function AccordionTrigger({
  className,
  children,
  ...props
}: AccordionTriggerProps) {
  const { onValueChange } = useAccordionContext();
  const { value, isOpen, disabled } = useAccordionItemContext();

  return (
    <button
      type="button"
      aria-expanded={isOpen}
      aria-controls={`accordion-content-${value}`}
      disabled={disabled}
      onClick={() => onValueChange(value)}
      className={clsx(
        'accordion-trigger flex items-center justify-between w-full py-4 px-1',
        'text-left font-medium',
        'text-gray-900 dark:text-gray-100',
        'hover:underline focus:outline-none',
        'focus-visible:ring-2 focus-visible:ring-blue-500',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      {...props}
    >
      {children}
      <svg
        className={clsx(
          'w-4 h-4 transition-transform duration-200',
          isOpen && 'rotate-180'
        )}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>
  );
}

// ============================================================================
// AccordionContent
// ============================================================================

export interface AccordionContentProps extends React.HTMLAttributes<HTMLDivElement> {
  forceMount?: boolean;
}

export function AccordionContent({
  forceMount = false,
  className,
  children,
  ...props
}: AccordionContentProps) {
  const { value, isOpen } = useAccordionItemContext();

  if (!forceMount && !isOpen) return null;

  return (
    <div
      id={`accordion-content-${value}`}
      role="region"
      aria-labelledby={`accordion-trigger-${value}`}
      hidden={!isOpen}
      className={clsx(
        'accordion-content pb-4 text-sm text-gray-600 dark:text-gray-300',
        'animate-in fade-in slide-in-from-top-2 duration-200',
        !isOpen && 'hidden',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// SimpleTabs (Convenience Component)
// ============================================================================

export interface SimpleTabsProps {
  tabs: TabItem[];
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  variant?: TabsVariant;
  size?: TabsSize;
  className?: string;
}

export function SimpleTabs({
  tabs,
  defaultValue,
  value,
  onValueChange,
  variant = 'default',
  size = 'md',
  className,
}: SimpleTabsProps) {
  const firstTabValue = defaultValue || tabs[0]?.id || '';

  return (
    <Tabs
      defaultValue={firstTabValue}
      value={value}
      onValueChange={onValueChange}
      variant={variant}
      size={size}
      className={className}
    >
      <TabsList>
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.id}
            value={tab.id}
            disabled={tab.disabled}
            icon={tab.icon}
          >
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>

      {tabs.map((tab) => (
        <TabsContent key={tab.id} value={tab.id} className="mt-4">
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  );
}

// ============================================================================
// VerticalTabs
// ============================================================================

export interface VerticalTabsProps {
  tabs: TabItem[];
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
}

export function VerticalTabs({
  tabs,
  defaultValue,
  value,
  onValueChange,
  className,
}: VerticalTabsProps) {
  const firstTabValue = defaultValue || tabs[0]?.id || '';

  return (
    <Tabs
      defaultValue={firstTabValue}
      value={value}
      onValueChange={onValueChange}
      orientation="vertical"
      className={clsx('flex gap-4', className)}
    >
      <TabsList className="flex-shrink-0 w-48 border-r border-gray-200 dark:border-gray-700 pr-4">
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.id}
            value={tab.id}
            disabled={tab.disabled}
            icon={tab.icon}
            className="w-full justify-start"
          >
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>

      <div className="flex-1">
        {tabs.map((tab) => (
          <TabsContent key={tab.id} value={tab.id}>
            {tab.content}
          </TabsContent>
        ))}
      </div>
    </Tabs>
  );
}

// ============================================================================
// SimpleAccordion (Convenience Component)
// ============================================================================

export interface AccordionItemData {
  id: string;
  title: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
}

export interface SimpleAccordionProps {
  items: AccordionItemData[];
  type?: AccordionType;
  defaultValue?: string | string[];
  collapsible?: boolean;
  className?: string;
}

export function SimpleAccordion({
  items,
  type = 'single',
  defaultValue,
  collapsible = false,
  className,
}: SimpleAccordionProps) {
  if (type === 'single') {
    return (
      <Accordion
        type="single"
        defaultValue={defaultValue as string}
        collapsible={collapsible}
        className={className}
      >
        {items.map((item) => (
          <AccordionItem key={item.id} value={item.id} disabled={item.disabled}>
            <AccordionTrigger>{item.title}</AccordionTrigger>
            <AccordionContent>{item.content}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    );
  }

  return (
    <Accordion
      type="multiple"
      defaultValue={defaultValue as string[]}
      className={className}
    >
      {items.map((item) => (
        <AccordionItem key={item.id} value={item.id} disabled={item.disabled}>
          <AccordionTrigger>{item.title}</AccordionTrigger>
          <AccordionContent>{item.content}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

// ============================================================================
// FAQ (FAQ-style Accordion)
// ============================================================================

export interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

export interface FAQProps {
  items: FAQItem[];
  className?: string;
}

export function FAQ({ items, className }: FAQProps) {
  return (
    <Accordion type="single" collapsible className={clsx('faq', className)}>
      {items.map((item, index) => (
        <AccordionItem key={index} value={`faq-${index}`}>
          <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
          <AccordionContent>{item.answer}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

// ============================================================================
// Collapsible
// ============================================================================

export interface CollapsibleProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export function Collapsible({
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  trigger,
  children,
  className,
}: CollapsibleProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const handleToggle = () => {
    const newOpen = !open;
    if (!isControlled) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  return (
    <div className={clsx('collapsible', className)}>
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={open}
        className="collapsible-trigger w-full text-left"
      >
        {trigger}
      </button>

      {open && (
        <div className="collapsible-content animate-in fade-in slide-in-from-top-2 duration-200">
          {children}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// useTabs Hook
// ============================================================================

export interface UseTabsOptions {
  defaultValue?: string;
  onValueChange?: (value: string) => void;
}

export interface UseTabsReturn {
  value: string;
  setValue: (value: string) => void;
  getTabsProps: () => Partial<TabsProps>;
  getTriggerProps: (value: string) => {
    value: string;
    onClick: () => void;
    'aria-selected': boolean;
  };
}

export function useTabs(options: UseTabsOptions = {}): UseTabsReturn {
  const { defaultValue = '', onValueChange } = options;
  const [value, setInternalValue] = React.useState(defaultValue);

  const setValue = React.useCallback(
    (newValue: string) => {
      setInternalValue(newValue);
      onValueChange?.(newValue);
    },
    [onValueChange]
  );

  const getTabsProps = React.useCallback(
    (): Partial<TabsProps> => ({
      value,
      onValueChange: setValue,
    }),
    [value, setValue]
  );

  const getTriggerProps = React.useCallback(
    (triggerValue: string) => ({
      value: triggerValue,
      onClick: () => setValue(triggerValue),
      'aria-selected': value === triggerValue,
    }),
    [value, setValue]
  );

  return {
    value,
    setValue,
    getTabsProps,
    getTriggerProps,
  };
}

// ============================================================================
// useAccordion Hook
// ============================================================================

export interface UseAccordionSingleOptions {
  type: 'single';
  defaultValue?: string;
  collapsible?: boolean;
  onValueChange?: (value: string) => void;
}

export interface UseAccordionMultipleOptions {
  type: 'multiple';
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
}

export type UseAccordionOptions = UseAccordionSingleOptions | UseAccordionMultipleOptions;

export interface UseAccordionSingleReturn {
  value: string;
  setValue: (value: string) => void;
  toggle: (itemValue: string) => void;
  isOpen: (itemValue: string) => boolean;
}

export interface UseAccordionMultipleReturn {
  value: string[];
  setValue: (value: string[]) => void;
  toggle: (itemValue: string) => void;
  isOpen: (itemValue: string) => boolean;
  openAll: () => void;
  closeAll: () => void;
}

export function useAccordion(options: UseAccordionSingleOptions): UseAccordionSingleReturn;
export function useAccordion(options: UseAccordionMultipleOptions): UseAccordionMultipleReturn;
export function useAccordion(options: UseAccordionOptions): UseAccordionSingleReturn | UseAccordionMultipleReturn {
  if (options.type === 'single') {
    return useAccordionSingle(options);
  }
  return useAccordionMultiple(options);
}

function useAccordionSingle(options: UseAccordionSingleOptions): UseAccordionSingleReturn {
  const { defaultValue = '', collapsible = false, onValueChange } = options;
  const [value, setInternalValue] = React.useState(defaultValue);

  const setValue = React.useCallback(
    (newValue: string) => {
      setInternalValue(newValue);
      onValueChange?.(newValue);
    },
    [onValueChange]
  );

  const toggle = React.useCallback(
    (itemValue: string) => {
      const newValue = value === itemValue && collapsible ? '' : itemValue;
      setValue(newValue);
    },
    [value, collapsible, setValue]
  );

  const isOpen = React.useCallback(
    (itemValue: string) => value === itemValue,
    [value]
  );

  return {
    value,
    setValue,
    toggle,
    isOpen,
  };
}

function useAccordionMultiple(options: UseAccordionMultipleOptions): UseAccordionMultipleReturn {
  const { defaultValue = [], onValueChange } = options;
  const [value, setInternalValue] = React.useState<string[]>(defaultValue);

  const setValue = React.useCallback(
    (newValue: string[]) => {
      setInternalValue(newValue);
      onValueChange?.(newValue);
    },
    [onValueChange]
  );

  const toggle = React.useCallback(
    (itemValue: string) => {
      const newValue = value.includes(itemValue)
        ? value.filter((v) => v !== itemValue)
        : [...value, itemValue];
      setValue(newValue);
    },
    [value, setValue]
  );

  const isOpen = React.useCallback(
    (itemValue: string) => value.includes(itemValue),
    [value]
  );

  const openAll = React.useCallback(() => {
    // This would need access to all item values
    // In practice, you'd pass the items as an option
  }, []);

  const closeAll = React.useCallback(() => {
    setValue([]);
  }, [setValue]);

  return {
    value,
    setValue,
    toggle,
    isOpen,
    openAll,
    closeAll,
  };
}
